import React, { useState, useEffect, useCallback, useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';

// MUI Components
import {
    Drawer,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField, Button, Switch, FormControlLabel,
    Grid, Chip, IconButton, Box, Typography, Divider, CircularProgress, Autocomplete,
    Tabs, Tab, Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Dialog
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import LabelDirectorySelect from "../labels/LabelDirectorySelect";
import OpenImageDrawerButton from "../labels/OpenImageDrawerButton"; // For Analyzer hint
import CustomModelConfigurationPanel from '../models/CustomModelConfigurationPanel';
import {
    buildCustomModelValue,
    buildCustomProviderOptionValue,
    buildCustomProviderOptions,
    deriveLegacyModelFlags,
    getCustomProvider,
    isCustomModelValue,
    normalizeCustomModelConfig,
    normalizeModelConfig,
    parseCustomModelValue,
    resolveModelPickerOption,
} from '../models/modelConfig';
const MAX_SELECTION = 9;

// --- Default Data (Should ideally come from config/props) ---
const defaultAvailableModels = [
    // { value: 'yolov4', label: 'YOLOv4', prompt: 0, captioning: 0 },
];
// Example data structures expected from config props
const defaultConfig = {
    tagsUrl: '',
    availableModels: [], // Array of { id/value, name/label, prompt, captioning }
    analyzerModels: [],  // Array of { id/value, name/label, remaining, hint }
    detectorModels: []   // Array of { id/value, name/label }
};

const defaultAdvancedSettings = {
    model: '', // Changed default
    model_classes: [], model_thresholds: [], union: [], analyzers: [], detectors: [],
    model_alarms: [], model_count: [], model_fixed: [], /* model_anonymize: [], */ model_tracked: [],
    text_reasoning: '',text_reasoning_prompt:'Please describe the scene, the objects and the actions in detail.', text_prompt: '', model_options: {},
    model_capabilities: undefined, model_metadata: undefined,
};
// --- End Default Data ---

// --- Helper Functions ---
const getTagName = (tagId, allTags) => allTags.find(t => t.id?.toString() === tagId?.toString())?.name || `ID: ${tagId}`;
const getTagColor = (tagId, allTags) => allTags.find(t => t.id?.toString() === tagId?.toString())?.color || `#cccccc`;
const mapToOptions = (items, valueKey = 'id', labelKey = 'name', includeRemaining = false) => {
    const normalizedItems = Array.isArray(items)
        ? items
        : (items && typeof items === 'object' ? Object.values(items) : []);

    return normalizedItems.map(item => ({
    label: item[labelKey] || item.label,
    value: item[valueKey] ?? item.value ?? item[labelKey] ?? item.label,
    ...(includeRemaining && { remaining: item.remaining }), // Conditionally include remaining
    ...item // Include other properties like prompt, captioning, hint, threshold
    }));
};
const getAutocompleteValue = (valueId, options) => options.find(opt => opt.name?.toString() === valueId?.toString()) || null;
const getMultiAutocompleteValue = (valueIds = [], options = []) => valueIds.map(id => options.find(opt => opt.value?.toString() === id?.toString())).filter(Boolean);

/**
 * Resolves legacy option flags for either internal or custom model selections.
 */
const getResolvedModelState = (modelValue, options = [], config = {}) => {
    const option = options.find((entry) => entry?.value === modelValue || entry?.name === modelValue) || null;
    const normalizedConfig = normalizeModelConfig({
        model_capabilities: config?.model_capabilities,
        model_metadata: config?.model_metadata,
    }, option);
    const customModel = parseCustomModelValue(modelValue);
    const flags = option
        ? {
            prompt: option?.prompt ? 1 : 0,
            captioning: option?.captioning_flag ? 1 : 0,
            model_image_prompt: option?.model_image_prompt ? 1 : 0,
        }
        : deriveLegacyModelFlags(normalizedConfig);

    return {
        option,
        customModel,
        config: normalizedConfig,
        flags,
    };
};

/**
 * Creates the default local form state for analyzer editing.
 */
const createEmptyAnalyzerForm = () => ({
    name: '',
    class: null,
    model: null,
    modelValue: '',
    model_capabilities: undefined,
    model_metadata: undefined,
    markerSet: null,
    values: '',
        text_prompt: '',
    cumulate: false,
    realtime: false,
    enrichment: false,
    get_images: false,
    buffering_image: false,
});

/**
 * Creates the default local form state for detector editing.
 */
const createEmptyDetectorForm = () => ({
    name: '',
    class: null,
    model: null,
    modelValue: '',
    model_capabilities: undefined,
    model_metadata: undefined,
    alarms: [],
    no_alarms: [],
        text_prompt: '',
    classes: [],
    buffering_image_enabled: false,
    buffering_image: [],
});

/**
 * Normalizes stored tag ids into the string format expected by the UI.
 */
const normalizeTagIdArray = (values = []) => {
    if (!Array.isArray(values)) {
        return [];
    }

    return values
        .map((value) => value?.toString())
        .filter(Boolean);
};

/**
 * Builds the complete set of detector tag ids allowed for buffering images.
 */
const getDetectorBufferingAllowedIds = ({ classes = [], alarms = [], no_alarms = [] } = {}) => {
    return Array.from(new Set([
        ...normalizeTagIdArray(classes),
        ...normalizeTagIdArray(alarms),
        ...normalizeTagIdArray(no_alarms),
    ]));
};

/**
 * Limits detector buffering classes to the detector's selected classes and alarm lists.
 */
const normalizeDetectorBufferingImage = (values = [], detectorConfig = {}) => {
    const allowedLookup = new Set(getDetectorBufferingAllowedIds(detectorConfig));

    return normalizeTagIdArray(values).filter((value, index, self) => (
        allowedLookup.has(value) && self.indexOf(value) === index
    ));
};
// --- End Helper Functions ---

// --- TabPanel Component ---
function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} id={`settings-tabpanel-${index}`} aria-labelledby={`settings-tab-${index}`} {...other}>
            {value === index && (
                <Box sx={{ pt: 3 }}> {/* Add padding top */}
                    {children}
                </Box>
            )}
        </div>
    );
}
// --- End TabPanel Component ---

// --- Hint Dialog Component (New) ---
function HintDialog({ open, onClose, hintText, hintImageUrl }) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md">
            <DialogTitle>Analyzer Hint</DialogTitle>
            <DialogContent dividers>
                {hintImageUrl && (
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                        <img src={hintImageUrl} alt="Hint" style={{ maxWidth: '100%', maxHeight: '60vh' }} />
                    </Box>
                )}
                {hintText && (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {hintText}
                    </Typography>
                )}
                {!hintText && !hintImageUrl && (
                    <Typography>No hint available for this model.</Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
// --- End Hint Dialog Component ---

function ZoneAdvancedSettingsModal({
    open,
    onClose,
    zoneData,
    zoneIndex,
    onSave,
    config = defaultConfig,
    allTags = [],
    availableMarkerSets = []
}) {
    const [advancedSettings, setAdvancedSettings] = useState(() => cloneDeep(defaultAdvancedSettings));
    const [tags, setTags] = useState([]); // Tags for the selected main model
    const [isLoadingTags, setIsLoadingTags] = useState(false);
    const [showThresholdTable, setShowThresholdTable] = useState(false);
    const [activeTab, setActiveTab] = useState(0); // State for active tab index

    // --- Form States ---
    const [unionFromSelection, setUnionFromSelection] = useState(null);
    const [unionToSelection, setUnionToSelection] = useState([]);
    const [analyzerForm, setAnalyzerForm] = useState(() => createEmptyAnalyzerForm());
    const [detectorForm, setDetectorForm] = useState(() => createEmptyDetectorForm());
    const [editingAnalyzerIndex, setEditingAnalyzerIndex] = useState(null);
    const [editingDetectorIndex, setEditingDetectorIndex] = useState(null);
    const [detectorModelTags, setDetectorModelTags] = useState([]); // Tags for the selected detector model
    const [isLoadingDetectorTags, setIsLoadingDetectorTags] = useState(false);

    // --- Hint Modal State (New) ---
    const [hintModalOpen, setHintModalOpen] = useState(false);
    const [currentHintData, setCurrentHintData] = useState({ text: '', imageUrl: null });
    // --- End Hint Modal State ---

    // --- Model/Tag Option Lists (derived from props/state) ---
    const availableModelOptions = mapToOptions(config.availableModels, 'id', 'name');
    const analyzerModelOptions = mapToOptions(config.analyzerModels, 'name', 'name', true);
    const detectorModelOptions = mapToOptions(config.detectorModels, 'name', 'name');
    const canManageCustomModels = !!config.isAdmin;
    const customProviderOptions = useMemo(() => {
        const options = canManageCustomModels ? buildCustomProviderOptions() : [];
        const visibleProviders = new Set(options.map((option) => option.provider));

        [advancedSettings.model, analyzerForm.modelValue, detectorForm.modelValue].forEach((modelValue) => {
            const customModel = parseCustomModelValue(modelValue);
            if (!customModel || visibleProviders.has(customModel.provider)) {
                return;
            }

            const providerInfo = getCustomProvider(customModel.provider);
            if (!providerInfo) {
                return;
            }

            options.push({
                label: providerInfo.label,
                value: buildCustomProviderOptionValue(customModel.provider),
                provider: customModel.provider,
                isCustomProvider: true,
            });
            visibleProviders.add(customModel.provider);
        });

        return options;
    }, [advancedSettings.model, analyzerForm.modelValue, canManageCustomModels, detectorForm.modelValue]);
    const allTagOptions_derived = mapToOptions(allTags); // Use 'id' and 'name' by default
    const markerSetOptions = mapToOptions(availableMarkerSets);
    const analyzerResolvedModel = useMemo(() => getResolvedModelState(analyzerForm.modelValue, analyzerModelOptions, analyzerForm), [analyzerForm, analyzerModelOptions]);
    const detectorResolvedModel = useMemo(() => getResolvedModelState(detectorForm.modelValue, detectorModelOptions, detectorForm), [detectorForm, detectorModelOptions]);
    const mainResolvedModel = useMemo(() => getResolvedModelState(advancedSettings.model, availableModelOptions, advancedSettings), [advancedSettings, availableModelOptions]);
    const analyzerRequiresMarkerSet = !!analyzerResolvedModel.option?.use_markers;
    const analyzerSupportsEnrichment = !!analyzerResolvedModel.option?.enrichment_enabled;
    const markerSetSelectDisabled = markerSetOptions.length === 0;
    const mainModelOptions_derived = [...availableModelOptions, ...customProviderOptions];
    const analyzerModelPickerOptions = [...analyzerModelOptions, ...customProviderOptions];
    const detectorModelPickerOptions = [...detectorModelOptions, ...customProviderOptions];
    const tagOptions_derived = isCustomModelValue(advancedSettings.model) ? allTagOptions_derived : mapToOptions(tags); // Main model tags

    // Options for secondary selects (Tracked, Count, Fixed, Alarm) MUST be derived from selected main model classes
    const selectedMainModelClassOptions = allTagOptions_derived.filter(opt => advancedSettings.model_classes?.includes(opt.value?.toString()));

    // Options for detector selects (Alarms, No Alarms, Classes) MUST be derived from tags of selected detector model
    const detectorTagOptions_derived = mapToOptions(detectorModelTags);
    const detectorBufferingAllowedIds = getDetectorBufferingAllowedIds(detectorForm);
    const detectorBufferingImageOptions = detectorTagOptions_derived.filter((option) => detectorBufferingAllowedIds.includes(option.value?.toString()));

    // Options for union select (From) MUST be derived from tracked tags (Modal + Main Page)
    const combinedTrackedTags = [
        ...(advancedSettings.model_tracked || []),
        ...(config.mainPageTrackedTags || [])
    ].filter((value, index, self) => self.indexOf(value) === index); // Unique IDs
    const unionFromOptions = allTagOptions_derived.filter(opt => combinedTrackedTags.includes(opt.value?.toString()));

    // Options for union select (To) MUST be derived from main model classes (Modal + Main Page)
    const combinedSelectedTags = [
        ...(advancedSettings.model_classes || []),
        ...(config.mainPageSelectedTags || [])
    ].filter((value, index, self) => self.indexOf(value) === index); // Unique IDs
    const unionToOptions = allTagOptions_derived.filter(opt => combinedSelectedTags.includes(opt.value?.toString()));

    // labels directory selection section
    const root = document.getElementById('labeled-files-root');
    const labels = root ? JSON.parse(root.dataset.labels || '[]') : [];
    const imagesViewUrl = root?.dataset.imagesViewUrl;
    const isDisabled = false;

    const markerSetLookup = useMemo(() => {
        const lookup = new Map();
        markerSetOptions.forEach((option) => {
            const key = option?.value != null ? option.value.toString() : null;
            if (key) {
                lookup.set(key, option.label || option.name || `Marker Set ${key}`);
            }
        });
        return lookup;
    }, [markerSetOptions]);

    const getMarkerSetName = useCallback((id) => {
        if (!id && id !== 0) {
            return null;
        }
        return markerSetLookup.get(id.toString()) || null;
    }, [markerSetLookup]);

    const [selectedPath, setSelectedPath] = useState(advancedSettings?.image_prompt_directory??'');
    const [selectedFiles, setSelectedFiles] = useState(advancedSettings?.image_prompt_files??[]);

        // --- Initialization Effect ---
    useEffect(() => {
        if (open && zoneData) {
            let initialSettings;
            try {
                const parsedSettings = typeof zoneData.advanced_settings === 'string'
                    ? JSON.parse(zoneData.advanced_settings || '{}')
                    : (zoneData.advanced_settings || {});
                initialSettings = { ...cloneDeep(defaultAdvancedSettings), ...parsedSettings };
                Object.keys(defaultAdvancedSettings).forEach(key => {
                    if (Array.isArray(defaultAdvancedSettings[key]) && !Array.isArray(initialSettings[key])) {
                        initialSettings[key] = [];
                    }
                });
                // Migrate model_options to model_thresholds if necessary (from older format)
                if (initialSettings.model_options && Object.keys(initialSettings.model_options).length > 0 && (!initialSettings.model_thresholds || initialSettings.model_thresholds.length === 0)) {
                initialSettings.model_thresholds = initialSettings.model_classes.map(cls => initialSettings.model_options[cls] ?? 0.5);
                }
                delete initialSettings.model_options; // Remove old key
                if (isCustomModelValue(initialSettings.model)) {
                    const normalizedCustomConfig = normalizeCustomModelConfig(initialSettings);
                    initialSettings.model_capabilities = normalizedCustomConfig.model_capabilities;
                    initialSettings.model_metadata = normalizedCustomConfig.model_metadata;
                }
                initialSettings.analyzers = Array.isArray(initialSettings.analyzers)
                    ? initialSettings.analyzers.map((entry) => {
                        const normalizedEntry = {
                            ...entry,
                            buffering_image: !!entry?.buffering_image,
                        };

                        return isCustomModelValue(entry?.model)
                            ? {
                                ...normalizedEntry,
                                ...normalizeCustomModelConfig(entry),
                            }
                            : normalizedEntry;
                    })
                    : [];
                initialSettings.detectors = Array.isArray(initialSettings.detectors)
                    ? initialSettings.detectors.map((entry) => {
                        const normalizedEntry = {
                            ...entry,
                            classes: normalizeTagIdArray(entry?.classes),
                            alarms: normalizeTagIdArray(entry?.alarms),
                            no_alarms: normalizeTagIdArray(entry?.no_alarms),
                        };

                        normalizedEntry.buffering_image = normalizeDetectorBufferingImage(entry?.buffering_image, normalizedEntry);

                        return isCustomModelValue(entry?.model)
                            ? {
                                ...normalizedEntry,
                                ...normalizeCustomModelConfig(entry),
                            }
                            : normalizedEntry;
                    })
                    : [];

            } catch (e) {
                console.error("Error parsing existing advanced settings, using defaults.", e);
                initialSettings = cloneDeep(defaultAdvancedSettings);
            }

            setAdvancedSettings(initialSettings);
            setShowThresholdTable(false);
            if (initialSettings.model) fetchTagsForModel(initialSettings.model);
            else setTags([]);
            // set files
            setSelectedPath(initialSettings?.image_prompt_directory??'');
            setSelectedFiles(initialSettings?.image_prompt_files??[]);

            // Reset forms and tabs
            setActiveTab(0);
            setUnionFromSelection(null); setUnionToSelection([]);
            setAnalyzerForm(createEmptyAnalyzerForm());
            setDetectorForm(createEmptyDetectorForm());
            setEditingAnalyzerIndex(null);
            setEditingDetectorIndex(null);
            setDetectorModelTags([]);
        } else if (!open) {
             // Reset non-persistent state when drawer is closed
             setAdvancedSettings(cloneDeep(defaultAdvancedSettings));
             setTags([]); setIsLoadingTags(false); setShowThresholdTable(false); setActiveTab(0);
             setUnionFromSelection(null); setUnionToSelection([]);
             setAnalyzerForm(createEmptyAnalyzerForm());
             setDetectorForm(createEmptyDetectorForm());
             setEditingAnalyzerIndex(null);
             setEditingDetectorIndex(null);
             setDetectorModelTags([]); setIsLoadingDetectorTags(false);
        }
    }, [open, zoneData, config.tagsUrl]);

    // --- Data Fetching Callbacks ---
     const fetchTagsForModel = useCallback(async (modelId) => {
        if (!modelId || !config.tagsUrl) { setTags([]); return; }
        if (isCustomModelValue(modelId)) {
            setTags(allTags);
            return;
        }
        setIsLoadingTags(true);
        try {
            const response = await fetch(`${config.tagsUrl}?model=${modelId}`);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            const sortedTags = (data.tags || []).sort((a, b) => a.name.localeCompare(b.name));
            setTags(sortedTags);
        } catch (error) { console.error('Error fetching tags for model:', error); setTags([]); }
        finally { setIsLoadingTags(false); }
    }, [allTags, config.tagsUrl]);
     const fetchTagsForDetectorModel = useCallback(async (modelId) => {
         if (!modelId || !config.tagsUrl) { setDetectorModelTags([]); return; }
         if (isCustomModelValue(modelId)) { setDetectorModelTags(allTags); return; }
         setIsLoadingDetectorTags(true);
         try {
             const response = await fetch(`${config.tagsUrl}?model=${modelId}`);
             if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
             const data = await response.json();
              const sortedTags = (data.tags || []).sort((a, b) => a.name.localeCompare(b.name));
             setDetectorModelTags(sortedTags);
         } catch (error) { console.error('Error fetching tags for detector model:', error); setDetectorModelTags([]); }
         finally { setIsLoadingDetectorTags(false); }
    }, [allTags, config.tagsUrl]);

    // --- Handlers ---
    const handleTabChange = (event, newValue) => setActiveTab(newValue);

    const handleMainModelChange = async (event, newValue) => {
        const previousCustomModel = parseCustomModelValue(advancedSettings.model);
        const newModelId = newValue?.isCustomProvider
            ? buildCustomModelValue(newValue.provider, previousCustomModel?.provider === newValue.provider ? previousCustomModel.modelName : '')
            : (newValue?.name || newValue?.value || '');
        console.log('newmodel',newValue);
        const oldModelId = advancedSettings.model;
        const oldClasses = advancedSettings.model_classes;
        const oldThresholds = advancedSettings.model_thresholds;

        if (newModelId === oldModelId) return;

        let newTags = [];
        if (newModelId) {
            if (isCustomModelValue(newModelId)) {
                newTags = [...allTags].sort((a, b) => a.name.localeCompare(b.name));
                setTags(newTags);
            } else {
                setIsLoadingTags(true);
                try {
                    const response = await fetch(`${config.tagsUrl}?model=${newModelId}`);
                    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                    const data = await response.json();
                    newTags = (data.tags || []).sort((a, b) => a.name.localeCompare(b.name));
                    setTags(newTags);
                } catch (error) {
                    console.error('Error fetching tags for new model:', error);
                    setTags([]);
                    newTags = [];
                } finally {
                    setIsLoadingTags(false);
                }
            }
        } else {
            setTags([]);
        }

        const newTagIds = newTags.map(tag => tag.id.toString());
        const compatibleClasses = oldClasses.filter(oldClassId => newTagIds.includes(oldClassId));
        const compatibleThresholds = compatibleClasses.map(classId => {
            const oldIndex = oldClasses.indexOf(classId);
            return oldThresholds[oldIndex];
        });

        const filterDependentTags = (dependentArray) => (dependentArray || []).filter(tagId => compatibleClasses.includes(tagId));

        setAdvancedSettings(prev => ({
            ...prev,
            model: newModelId || '',
            model_capabilities: newValue?.isCustomProvider ? normalizeCustomModelConfig(prev).model_capabilities : undefined,
            model_metadata: newValue?.isCustomProvider ? normalizeCustomModelConfig(prev).model_metadata : undefined,
            model_classes: compatibleClasses,
            model_thresholds: compatibleThresholds,
            model_tracked: filterDependentTags(prev.model_tracked),
            model_count: filterDependentTags(prev.model_count),
            model_fixed: filterDependentTags(prev.model_fixed),
            model_alarms: filterDependentTags(prev.model_alarms),
            union: [],
            text_prompt: '',
            text_reasoning: '',
            text_reasoning_prompt:'Please describe the scene, the objects and the actions in detail.'
        }));

        setShowThresholdTable(false);
        setUnionFromSelection(null); setUnionToSelection([]);
    };

    const handleMainTagSelectionChange = (event, newValue) => {
        const selectedTagIds = newValue ? newValue.map(opt => opt.value.toString()) : [];
        const newThresholds = selectedTagIds.map(id => {
            const existingIndex = advancedSettings.model_classes.indexOf(id);
            if (existingIndex !== -1 && advancedSettings.model_thresholds[existingIndex] != null) {
                return advancedSettings.model_thresholds[existingIndex];
            }
            const tagData = tags.find(t => t.id.toString() === id);
            return tagData?.threshold ?? 0.5;
        });

        const filterDependentTags = (dependentArray) => (dependentArray || []).filter(tagId => selectedTagIds.includes(tagId));

        setAdvancedSettings(prev => ({
            ...prev,
            model_classes: selectedTagIds,
            model_thresholds: newThresholds,
            model_tracked: filterDependentTags(prev.model_tracked),
            model_count: filterDependentTags(prev.model_count),
            model_fixed: filterDependentTags(prev.model_fixed),
            model_alarms: filterDependentTags(prev.model_alarms),
            union: [],
        }));
        setShowThresholdTable(false);
        setUnionFromSelection(null); setUnionToSelection([]);
    };

    const handleThresholdChange = (tagIndex, event) => {
        let newThreshold = parseFloat(event.target.value);
        if (!isNaN(newThreshold)) {
            newThreshold = Math.max(0, Math.min(1, newThreshold));
            setAdvancedSettings(prev => {
                const updatedThresholds = [...prev.model_thresholds];
                updatedThresholds[tagIndex] = newThreshold;
                return { ...prev, model_thresholds: updatedThresholds };
            });
        } else if (event.target.value === '') {
             setAdvancedSettings(prev => {
                const updatedThresholds = [...prev.model_thresholds];
                updatedThresholds[tagIndex] = '';
                return { ...prev, model_thresholds: updatedThresholds };
            });
        }
    };

    const handleTextPromptChange = (event) => {
        const text = event.target.value;
        setAdvancedSettings(prev => ({ ...prev, text_prompt: text }));
        if (text && tags.length > 0) {
            let currentSelectedIds = [...advancedSettings.model_classes];
            const localPromptWords = text.toLowerCase().replace(/[,.]/g, ' ').split(/\s+/).filter(Boolean);
            tags.forEach(tag => {
                if (localPromptWords.includes(tag.name.toLowerCase())) {
                    if (!currentSelectedIds.includes(tag.id.toString())) {
                        currentSelectedIds.push(tag.id.toString());
                    }
                }
            });
            const newAutocompleteValues = getMultiAutocompleteValue(currentSelectedIds, tagOptions_derived);
            handleMainTagSelectionChange(null, newAutocompleteValues);
        }
    };

    const handleArraySelectionChange = (field, event, newValue) => {
        setAdvancedSettings(prev => ({ ...prev, [field]: newValue ? newValue.map(opt => opt.value.toString()) : [] }));
        if (field === 'model_tracked' && unionFromSelection) {
             const newTrackedIds = newValue ? newValue.map(opt => opt.value.toString()) : [];
             if (!newTrackedIds.includes(unionFromSelection.value.toString())) {
                 setUnionFromSelection(null);
             }
         }
    };

    const handleSimpleSettingChange = (field, event) => {
         setAdvancedSettings(prev => ({ ...prev, [field]: event.target.value }));
     };

    // --- Union Handlers ---
    const handleAddUnion = () => {
        if (!unionFromSelection || unionToSelection.length === 0) return;
        const fromTagId = unionFromSelection.value.toString();
        const toTagIds = unionToSelection.map(opt => opt.value.toString()).filter(id => id !== fromTagId);
        if (toTagIds.length === 0) { alert("Cannot union a tag with itself."); return; }
        setAdvancedSettings(prev => {
             const filteredUnions = (prev.union || []).filter(u => u[0]?.toString() !== fromTagId);
             return { ...prev, union: [...filteredUnions, [fromTagId, toTagIds]] };
         });
        setUnionFromSelection(null); setUnionToSelection([]);
    };
    const handleDeleteUnion = (indexToDelete) => {
        if (confirm("Remove union?")) {
            setAdvancedSettings(prev => ({ ...prev, union: (prev.union || []).filter((_, i) => i !== indexToDelete) }));
        }
    };

    // --- Analyzer Handlers ---
    const handleAnalyzerFormChange = (field, value) => setAnalyzerForm(prev => ({ ...prev, [field]: value }));
    const handleAnalyzerFormAutocompleteChange = (field, event, newValue) => {
        if (field !== 'model') {
            setAnalyzerForm(prev => ({ ...prev, [field]: newValue }));
            return;
        }

        setAnalyzerForm(prev => {
            const previousCustom = parseCustomModelValue(prev.modelValue);
            const nextModelValue = newValue?.isCustomProvider
                ? buildCustomModelValue(newValue.provider, previousCustom?.provider === newValue.provider ? previousCustom.modelName : '')
                : (newValue?.value || '');
            const nextState = {
                ...prev,
                model: newValue,
                modelValue: nextModelValue,
                model_capabilities: newValue?.isCustomProvider ? normalizeCustomModelConfig(prev).model_capabilities : undefined,
                model_metadata: newValue?.isCustomProvider ? normalizeCustomModelConfig(prev).model_metadata : undefined,
            };
            if (!(newValue?.use_markers)) {
                nextState.markerSet = null;
            }
            if (!(newValue?.enrichment_enabled)) {
                nextState.enrichment = false;
            }
            return nextState;
        });
    };
    const handleAnalyzerFormSwitchChange = (field, event) => setAnalyzerForm(prev => ({ ...prev, [field]: event.target.checked }));
    const handleAnalyzerPaste = (event) => {
         try {
             const pastedData = event.clipboardData.getData('text');
             const values = pastedData.split(/[\n,]+/).map(v => v.trim()).filter(Boolean);
             if (values.length > 0) {
                 const currentVal = analyzerForm.values.split(',').map(v => v.trim()).filter(Boolean);
                 const newVal = [...currentVal, ...values].join(',');
                 setAnalyzerForm(prev => ({...prev, values: newVal}));
             }
             event.preventDefault();
         } catch (e) {
             console.error("Paste error:", e);
         }
     };
    const handleOpenHintModal = () => {
        if (analyzerResolvedModel.option) {
            setCurrentHintData({
                text: analyzerResolvedModel.option.hint || '',
                imageUrl: analyzerResolvedModel.option.hint_file_url || null
            });
            setHintModalOpen(true);
        }
    };
    const handleCloseHintModal = () => setHintModalOpen(false);
    const handleAddAnalyzer = () => {
        const analyzerModelValue = analyzerForm.modelValue || analyzerForm.model?.value || '';
        if (!analyzerForm.name || !analyzerForm.class?.value || !analyzerModelValue) { alert('Analyzer Name, Class, and Model required.'); return; }
        if (isCustomModelValue(analyzerModelValue) && !parseCustomModelValue(analyzerModelValue)?.modelName) {
            alert('Please provide the custom analyzer model identifier.');
            return;
        }
        const localSelectedModelData = analyzerResolvedModel.option;
        if (localSelectedModelData && localSelectedModelData.remaining !== undefined && localSelectedModelData.remaining <= 0) {
            alert('No remaining uses for this analyzer model.');
            return;
        }

        const markerSetId = analyzerForm.markerSet?.value ?? analyzerForm.markerSet?.id ?? null;
        if (analyzerRequiresMarkerSet && !markerSetId) {
            alert('Please select a marker set for this analyzer model.');
            return;
        }

        const newAnalyzer = {
            name: analyzerForm.name,
            class: analyzerForm.class.value.toString(),
            model: analyzerModelValue,
            values: analyzerForm.values,
            cumulate: analyzerForm.cumulate,
            realtime: analyzerForm.realtime,
            enrichment: analyzerForm.enrichment,
            text_prompt: (analyzerForm.text_prompt || '').trim(),
            get_images: analyzerForm.get_images,
            buffering_image: analyzerForm.buffering_image,
            ...(markerSetId ? { marker_set_id: markerSetId } : {}),
            ...(isCustomModelValue(analyzerModelValue) ? normalizeCustomModelConfig(analyzerForm) : {}),
        };

        setAdvancedSettings((prev) => {
            const analyzers = [...(prev.analyzers || [])];

            if (editingAnalyzerIndex !== null) {
                analyzers[editingAnalyzerIndex] = newAnalyzer;
            } else {
                analyzers.push(newAnalyzer);
            }

            return { ...prev, analyzers };
        });
        setAnalyzerForm(createEmptyAnalyzerForm());
        setEditingAnalyzerIndex(null);
    };
    /**
     * Loads an existing analyzer into the inline form for editing.
     */
    const handleEditAnalyzer = (index) => {
        const analyzer = advancedSettings.analyzers?.[index];

        if (!analyzer) {
            return;
        }

        const resolvedMarkerSetId = analyzer.marker_set_id ?? analyzer.marker_set ?? analyzer.markerSet?.id ?? analyzer.markerSet?.value;

        setAnalyzerForm({
            ...createEmptyAnalyzerForm(),
            name: analyzer.name || '',
            class: allTagOptions_derived.find((option) => option.value?.toString() === analyzer.class?.toString()) || null,
            model: resolveModelPickerOption(analyzer.model, analyzerModelPickerOptions),
            modelValue: analyzer.model || '',
            model_capabilities: analyzer.model_capabilities,
            model_metadata: analyzer.model_metadata,
            markerSet: markerSetOptions.find((option) => option.value?.toString() === resolvedMarkerSetId?.toString()) || null,
            values: analyzer.values || '',
            text_prompt: analyzer.text_prompt || analyzer.textprompt || '',
            cumulate: !!analyzer.cumulate,
            realtime: !!(analyzer.realtime ?? analyzer.realtime_analysis),
            enrichment: !!(analyzer.enrichment ?? analyzer.enrichment_analysis),
            get_images: !!(analyzer.get_images ?? analyzer.get_image),
            buffering_image: !!analyzer.buffering_image,
        });
        setEditingAnalyzerIndex(index);
        setActiveTab(3);
    };
    /**
     * Clears the analyzer inline edit state and returns to add mode.
     */
    const handleCancelAnalyzerEdit = () => {
        setAnalyzerForm(createEmptyAnalyzerForm());
        setEditingAnalyzerIndex(null);
    };
    const handleDeleteAnalyzer = (indexToDelete) => {
        if (confirm("Remove analyzer?")) {
             setAdvancedSettings(prev => ({ ...prev, analyzers: (prev.analyzers || []).filter((_, i) => i !== indexToDelete) }));
             if (editingAnalyzerIndex === indexToDelete) {
                 handleCancelAnalyzerEdit();
             } else if (editingAnalyzerIndex !== null && editingAnalyzerIndex > indexToDelete) {
                 setEditingAnalyzerIndex(editingAnalyzerIndex - 1);
             }
        }
    };

    // --- Detector Handlers ---
    const handleDetectorFormTextFieldChange = (field, event) => setDetectorForm(prev => ({ ...prev, [field]: event.target.value }));
    const handleDetectorFormAutocompleteChange = (field, event, newValue) => setDetectorForm(prev => ({ ...prev, [field]: newValue }));
    const handleDetectorFormSwitchChange = (field, event) => {
        const checked = event.target.checked;

        setDetectorForm((prev) => ({
            ...prev,
            [field]: checked,
            ...(field === 'buffering_image_enabled' && !checked ? { buffering_image: [] } : {}),
        }));
    };
    const handleDetectorModelChange = (event, newValue) => {
        setDetectorForm(prev => {
            const previousCustom = parseCustomModelValue(prev.modelValue);
            const nextModelValue = newValue?.isCustomProvider
                ? buildCustomModelValue(newValue.provider, previousCustom?.provider === newValue.provider ? previousCustom.modelName : '')
                : (newValue?.value || '');

            return {
                ...prev,
                model: newValue,
                modelValue: nextModelValue,
                model_capabilities: newValue?.isCustomProvider ? normalizeCustomModelConfig(prev).model_capabilities : undefined,
                model_metadata: newValue?.isCustomProvider ? normalizeCustomModelConfig(prev).model_metadata : undefined,
                alarms: [],
                no_alarms: [],
                classes: [],
                buffering_image_enabled: false,
                buffering_image: [],
            };
        });
        if (newValue?.value || newValue?.isCustomProvider) fetchTagsForDetectorModel(newValue?.isCustomProvider ? buildCustomModelValue(newValue.provider, '') : newValue.value);
        else setDetectorModelTags([]);
    };
    const handleDetectorMultiSelectChange = (field, event, newValue) => {
        const selectedValues = newValue ? newValue.map(opt => opt.value.toString()) : [];

        setDetectorForm((prev) => {
            if (field !== 'classes') {
                return { ...prev, [field]: selectedValues };
            }

            return {
                ...prev,
                classes: selectedValues,
                buffering_image: normalizeDetectorBufferingImage(prev.buffering_image, {
                    ...prev,
                    classes: selectedValues,
                }),
            };
        });
    };
    /**
     * Loads an existing detector into the inline form for editing.
     */
    const handleEditDetector = async (index) => {
        const detector = advancedSettings.detectors?.[index];

        if (!detector) {
            return;
        }

        await fetchTagsForDetectorModel(detector.model);

        const detectorClasses = normalizeTagIdArray(detector.classes);
        const detectorBufferingImage = normalizeDetectorBufferingImage(detector.buffering_image, {
            ...detector,
            classes: detectorClasses,
        });

        setDetectorForm({
            ...createEmptyDetectorForm(),
            name: detector.name || '',
            class: allTagOptions_derived.find((option) => option.value?.toString() === detector.class?.toString()) || null,
            model: resolveModelPickerOption(detector.model, detectorModelPickerOptions),
            modelValue: detector.model || '',
            model_capabilities: detector.model_capabilities,
            model_metadata: detector.model_metadata,
            text_prompt: detector.text_prompt || detector.textprompt || '',
            alarms: normalizeTagIdArray(detector.alarms),
            no_alarms: normalizeTagIdArray(detector.no_alarms),
            classes: detectorClasses,
            buffering_image_enabled: detectorBufferingImage.length > 0,
            buffering_image: detectorBufferingImage,
        });
        setEditingDetectorIndex(index);
        setActiveTab(4);
    };
    /**
     * Clears the detector inline edit state and returns to add mode.
     */
    const handleCancelDetectorEdit = () => {
        setDetectorForm(createEmptyDetectorForm());
        setEditingDetectorIndex(null);
        setDetectorModelTags([]);
    };
    const handleAddDetector = () => {
        const detectorModelValue = detectorForm.modelValue || detectorForm.model?.value || '';
        if (!detectorForm.name || !detectorForm.class?.value || !detectorModelValue) { alert('Detector Name, Class, and Model required.'); return; }
        if (isCustomModelValue(detectorModelValue) && !parseCustomModelValue(detectorModelValue)?.modelName) { alert('Please provide the custom detector model identifier.'); return; }

        const detectorClasses = normalizeTagIdArray(detectorForm.classes);
        const detectorBufferingImage = detectorForm.buffering_image_enabled
            ? normalizeDetectorBufferingImage(detectorForm.buffering_image, {
                ...detectorForm,
                classes: detectorClasses,
            })
            : [];

        const newDetector = {
            name: detectorForm.name,
            class: detectorForm.class.value.toString(),
            model: detectorModelValue,
            text_prompt: (detectorForm.text_prompt || '').trim(),
            alarms: detectorForm.alarms,
            no_alarms: detectorForm.no_alarms,
            classes: detectorClasses,
            buffering_image: detectorBufferingImage,
            ...(isCustomModelValue(detectorModelValue) ? normalizeCustomModelConfig(detectorForm) : {}),
        };

        setAdvancedSettings((prev) => {
            const detectors = [...(prev.detectors || [])];

            if (editingDetectorIndex !== null) {
                detectors[editingDetectorIndex] = newDetector;
            } else {
                detectors.push(newDetector);
            }

            return { ...prev, detectors };
        });
        setDetectorForm(createEmptyDetectorForm());
        setEditingDetectorIndex(null);
        setDetectorModelTags([]);
    };
     const handleDeleteDetector = (indexToDelete) => {
         if (confirm("Remove detector?")) {
             setAdvancedSettings(prev => ({ ...prev, detectors: (prev.detectors || []).filter((_, i) => i !== indexToDelete) }));
             if (editingDetectorIndex === indexToDelete) {
                 handleCancelDetectorEdit();
             } else if (editingDetectorIndex !== null && editingDetectorIndex > indexToDelete) {
                 setEditingDetectorIndex(editingDetectorIndex - 1);
             }
         }
     };

    const handleSave = () => {
        // Validate main detection model
        if (!advancedSettings.model) {
            alert("Please select a main detection model.");
            return;
        }
        if (isCustomModelValue(advancedSettings.model) && !parseCustomModelValue(advancedSettings.model)?.modelName) {
            alert('Please provide the custom main model identifier.');
            return;
        }

        // // Validate detected classes
        // if (!advancedSettings.model_classes || advancedSettings.model_classes.length === 0) {
        //     alert("Please select at least one detected class.");
        //     return;
        // }

        const selectedModelData = mainResolvedModel.option;

        // Validate captioning fields when captioning is enabled
        if (selectedModelData && (selectedModelData.captioning_flag === 1 || selectedModelData.captioning_flag === true)) {
            if (!advancedSettings.text_reasoning_prompt || advancedSettings.text_reasoning_prompt.trim() === '') {
                alert("Text Prompt (Captioning) is required for this model.");
                return;
            }
            if (!advancedSettings.text_reasoning || advancedSettings.text_reasoning.trim() === '') {
                alert("Text Reasoning (Captioning) is required for this model.");
                return;
            }
        }

        const finalSettings = cloneDeep(advancedSettings);
        finalSettings.image_prompt_files = selectedFiles;
        finalSettings.image_prompt_directory = selectedPath;
        if (!isCustomModelValue(finalSettings.model)) {
            delete finalSettings.model_capabilities;
            delete finalSettings.model_metadata;
        } else {
            Object.assign(finalSettings, normalizeCustomModelConfig(finalSettings));
        }

        finalSettings.model_thresholds = finalSettings.model_thresholds.map((thr, index) => {
             const numThr = parseFloat(thr);
             if (isNaN(numThr)) {
                 const tagId = finalSettings.model_classes[index];
                 const tagData = tags.find(t => t.id.toString() === tagId);
                 return tagData?.threshold ?? 0.5;
             }
             return numThr;
         });

        finalSettings.analyzers = (finalSettings.analyzers || []).map((analyzer) => ({
            ...analyzer,
            text_prompt: typeof analyzer?.text_prompt === 'string' ? analyzer.text_prompt.trim() : '',
            buffering_image: !!analyzer?.buffering_image,
        }));

        finalSettings.detectors = (finalSettings.detectors || []).map((detector) => {
            const classes = normalizeTagIdArray(detector.classes);

            return {
                ...detector,
                text_prompt: typeof detector?.text_prompt === 'string' ? detector.text_prompt.trim() : '',
                classes,
                alarms: normalizeTagIdArray(detector.alarms),
                no_alarms: normalizeTagIdArray(detector.no_alarms),
                buffering_image: normalizeDetectorBufferingImage(detector.buffering_image, {
                    ...detector,
                    classes,
                }),
            };
        });

         if (selectedModelData && !selectedModelData.captioning_flag) {
             finalSettings.text_reasoning = '';
         }
         if (selectedModelData && !selectedModelData.prompt && !selectedModelData.isCustomProvider) {
            finalSettings.text_prompt = '';
        }

        console.log("Saving settings:", finalSettings);
        onSave(zoneIndex, finalSettings);
        onClose();
    };

    const handleClose = () => {
        onClose();
    }

    const selectedModelData = mainResolvedModel.option;
    const showPromptInput = mainResolvedModel.flags.prompt === 1 || !!mainResolvedModel.customModel;
    const showPromptImage = mainResolvedModel.flags.model_image_prompt === 1;
    const showReasoningInput = mainResolvedModel.flags.captioning === 1;

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={handleClose}
            >
                <Box
                    sx={{ width: '80vw', maxWidth: 1200, p: 2 }}
                    role="presentation"
                >
                    <DialogTitle sx={{ m: 0, p: 0, mb: 2 }}>
                        Advanced Settings for Zone: {zoneData?.name || `Index ${zoneIndex+1}`}
                        <IconButton
                             aria-label="close"
                             onClick={handleClose}
                             sx={{
                                 position: 'absolute',
                                 right: 8,
                                 top: 8,
                                 color: (theme) => theme.palette.grey[500],
                             }}
                         >
                             <CloseIcon />
                         </IconButton>
                     </DialogTitle>

                    <DialogContent sx={{ p: 0 }}>
                         <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                             <Grid item xs={12} md={6}>
                                 <Autocomplete
                                    options={mainModelOptions_derived}
                                    getOptionDisabled={(option) => !canManageCustomModels && option?.isCustomProvider}
                                    getOptionLabel={(option) => option.label+(option?.prompt?"- (Prompt enabled)":"")+(option?.captioning_flag?"- (Support captioning)":"")+(option?.model_image_prompt?"- (Support Image Prompt)":"") || ''}
                                    value={resolveModelPickerOption(advancedSettings.model, mainModelOptions_derived)}
                                    onChange={handleMainModelChange}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    renderInput={(params) => <TextField {...params} label="Main Detection Model" variant="outlined" fullWidth />}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    multiple
                                    limitTags={3}
                                    options={tagOptions_derived}
                                    getOptionLabel={(option) => option.label || ''}
                                    value={getMultiAutocompleteValue(advancedSettings.model_classes, tagOptions_derived)}
                                    onChange={handleMainTagSelectionChange}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    loading={isLoadingTags}
                                    disabled={!advancedSettings.model}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Detected Classes"
                                            variant="outlined"
                                            fullWidth
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {isLoadingTags ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            {canManageCustomModels && mainResolvedModel.customModel && (
                                <Grid item xs={12}>
                                    <CustomModelConfigurationPanel
                                        provider={mainResolvedModel.customModel.provider}
                                        modelName={mainResolvedModel.customModel.modelName}
                                        config={mainResolvedModel.config}
                                        title="Zone custom model"
                                        onModelNameChange={(value) => setAdvancedSettings((prev) => ({
                                            ...prev,
                                            model: buildCustomModelValue(mainResolvedModel.customModel.provider, value),
                                        }))}
                                        onConfigChange={(value) => setAdvancedSettings((prev) => ({
                                            ...prev,
                                            model_capabilities: value.model_capabilities,
                                            model_metadata: value.model_metadata,
                                        }))}
                                    />
                                </Grid>
                            )}
                             {showPromptImage && (
                                 <Grid item xs={12}>
                                     <Box display="flex" flexDirection="column" gap={2}>
                                         <Box display="flex" alignItems="center" gap={2}>
                                             <LabelDirectorySelect
                                                 labels={labels}
                                                 value={selectedPath}
                                                 onChange={setSelectedPath}
                                                 disabled={isDisabled}
                                             />
                                             <OpenImageDrawerButton
                                                 selectedPath={selectedPath}
                                                 selectedFiles={selectedFiles}
                                                 disabled={isDisabled}
                                                 imagesViewUrl={imagesViewUrl}
                                                 maxSelection={MAX_SELECTION}
                                                 onSelectedChange={setSelectedFiles}
                                             />
                                         </Box>
                                     </Box>
                                 </Grid>
                             )}
                             {showPromptInput && (
                                <Grid item xs={12}>
                                    <TextField
                                        label="Text Prompt"
                                        value={advancedSettings.text_prompt || ''}
                                        onChange={handleTextPromptChange}
                                        fullWidth
                                        variant="outlined"
                                        helperText="Enter text prompt to automatically select matching classes."
                                    />
                                </Grid>
                            )}
                            {showReasoningInput && (<>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Text Prompt (Captioning)"
                                        value={advancedSettings.text_reasoning_prompt ?? 'Please describe the scene, the objects and the actions in detail.'}
                                        onChange={(e) => handleSimpleSettingChange('text_reasoning_prompt', e)}
                                        fullWidth
                                        required
                                        variant="outlined"
                                        placeholder="Prompt used to get the Cationing:'Please describe the scene, the objects and the actions in detail.'"
                                        multiline
                                        rows={2}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Text Reasoning (Captioning)"
                                        value={advancedSettings.text_reasoning || ''}
                                        onChange={(e) => handleSimpleSettingChange('text_reasoning', e)}
                                        fullWidth
                                        required
                                        variant="outlined"
                                        placeholder="Reasoning text must contain something that will trigger an alarm"
                                        multiline
                                        rows={2}
                                    />
                                </Grid>
                                </>
                            )}
                         </Grid>
                         <Divider sx={{ my: 2 }} />

                         <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={activeTab} onChange={handleTabChange} aria-label="advanced settings tabs">
                                 <Tab label="General Tags" id="settings-tab-0" aria-controls="settings-tabpanel-0" />
                                 <Tab label="Thresholds" id="settings-tab-1" aria-controls="settings-tabpanel-1" />
                                 <Tab label="Unions" id="settings-tab-2" aria-controls="settings-tabpanel-2" />
                                 <Tab label="Analyzers" id="settings-tab-3" aria-controls="settings-tabpanel-3" />
                                 <Tab label="Detectors" id="settings-tab-4" aria-controls="settings-tabpanel-4" />
                             </Tabs>
                         </Box>

                         <TabPanel value={activeTab} index={0}>
                             <Grid container spacing={3}>
                                 <Grid item xs={12} md={6}>
                                     <Autocomplete
                                         multiple limitTags={3}
                                         options={selectedMainModelClassOptions}
                                         getOptionLabel={(option) => option.label}
                                         value={getMultiAutocompleteValue(advancedSettings.model_tracked, selectedMainModelClassOptions)}
                                         onChange={(e, v) => handleArraySelectionChange('model_tracked', e, v)}
                                         isOptionEqualToValue={(option, value) => option.value === value.value}
                                         disabled={advancedSettings.model_classes.length === 0}
                                         renderTags={(value, getTagProps) =>
                                             value.map((option, index) => {
                                                 const { key, ...tagProps } = getTagProps({ index });
                                                 return (
                                                     <Chip
                                                         key={key}
                                                         variant="outlined"
                                                         label={option.label}
                                                         {...tagProps}
                                                     />
                                                 );
                                             })
                                         }
                                         renderInput={(params) => <TextField {...params} label="Tracked Tags" variant="outlined" fullWidth helperText="Tags available for Unions" />}
                                     />
                                 </Grid>
                                 <Grid item xs={12} md={6}>
                                      <Autocomplete
                                         multiple limitTags={3}
                                         options={selectedMainModelClassOptions}
                                         getOptionLabel={(option) => option.label}
                                         value={getMultiAutocompleteValue(advancedSettings.model_count, selectedMainModelClassOptions)}
                                         onChange={(e, v) => handleArraySelectionChange('model_count', e, v)}
                                         isOptionEqualToValue={(option, value) => option.value === value.value}
                                         disabled={advancedSettings.model_classes.length === 0}
                                         renderTags={(value, getTagProps) =>
                                             value.map((option, index) => {
                                                 const { key, ...tagProps } = getTagProps({ index });
                                                 return (
                                                     <Chip
                                                         key={key}
                                                         variant="outlined"
                                                         label={option.label}
                                                         {...tagProps}
                                                     />
                                                 );
                                             })
                                         }
                                         renderInput={(params) => <TextField {...params} label="Count Tags" variant="outlined" fullWidth />}
                                     />
                                 </Grid>
                                 <Grid item xs={12} md={6}>
                                      <Autocomplete
                                         multiple limitTags={3}
                                         options={selectedMainModelClassOptions}
                                         getOptionLabel={(option) => option.label}
                                         value={getMultiAutocompleteValue(advancedSettings.model_fixed, selectedMainModelClassOptions)}
                                         onChange={(e, v) => handleArraySelectionChange('model_fixed', e, v)}
                                         isOptionEqualToValue={(option, value) => option.value === value.value}
                                         disabled={advancedSettings.model_classes.length === 0}
                                         renderTags={(value, getTagProps) =>
                                             value.map((option, index) => {
                                                 const { key, ...tagProps } = getTagProps({ index });
                                                 return (
                                                     <Chip
                                                         key={key}
                                                         variant="outlined"
                                                         label={option.label}
                                                         {...tagProps}
                                                     />
                                                 );
                                             })
                                         }
                                         renderInput={(params) => <TextField {...params} label="Fixed Tags" variant="outlined" fullWidth />}
                                     />
                                 </Grid>
                                 <Grid item xs={12} md={6}>
                                      <Autocomplete
                                         multiple limitTags={3}
                                         options={selectedMainModelClassOptions}
                                         getOptionLabel={(option) => option.label}
                                         value={getMultiAutocompleteValue(advancedSettings.model_alarms, selectedMainModelClassOptions)}
                                         onChange={(e, v) => handleArraySelectionChange('model_alarms', e, v)}
                                         isOptionEqualToValue={(option, value) => option.value === value.value}
                                         disabled={advancedSettings.model_classes.length === 0}
                                         renderTags={(value, getTagProps) =>
                                             value.map((option, index) => {
                                                 const { key, ...tagProps } = getTagProps({ index });
                                                 return (
                                                     <Chip
                                                         key={key}
                                                         variant="outlined"
                                                         label={option.label}
                                                         {...tagProps}
                                                     />
                                                 );
                                             })
                                         }
                                         renderInput={(params) => <TextField {...params} label="Alarm Tags" variant="outlined" fullWidth />}
                                     />
                                 </Grid>
                             </Grid>
                         </TabPanel>

                         <TabPanel value={activeTab} index={1}>
                             <FormControlLabel
                                control={<Switch checked={showThresholdTable} onChange={(e) => setShowThresholdTable(e.target.checked)} />}
                                label="Edit Thresholds Manually"
                                disabled={advancedSettings.model_classes.length === 0}
                             />
                             {showThresholdTable && advancedSettings.model_classes.length > 0 && (
                                 <TableContainer component={Paper} sx={{ mt: 2 }}>
                                     <Table size="small">
                                         <TableHead>
                                             <TableRow>
                                                 <TableCell>#</TableCell>
                                                 <TableCell>Class</TableCell>
                                                 <TableCell>Threshold (0.0 - 1.0)</TableCell>
                                             </TableRow>
                                         </TableHead>
                                         <TableBody>
                                             {advancedSettings.model_classes.map((tagId, index) => (
                                                 <TableRow key={tagId}>
                                                     <TableCell>{index + 1}</TableCell>
                                                     <TableCell>{getTagName(tagId, allTags)}</TableCell>
                                                     <TableCell>
                                                         <TextField
                                                             type="number"
                                                             value={advancedSettings.model_thresholds[index] ?? ''}
                                                             onChange={(e) => handleThresholdChange(index, e)}
                                                             inputProps={{ step: "0.05", min: "0", max: "1" }}
                                                             variant="standard"
                                                             size="small"
                                                             sx={{ width: '100px' }}
                                                         />
                                                     </TableCell>
                                                 </TableRow>
                                             ))}
                                         </TableBody>
                                     </Table>
                                 </TableContainer>
                             )}
                         </TabPanel>

                        <TabPanel value={activeTab} index={2}>
                            <Typography variant="h6" gutterBottom>Create Union</Typography>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={5}>
                                    <Autocomplete
                                        options={unionFromOptions}
                                        getOptionLabel={(option) => option.label}
                                        value={unionFromSelection}
                                        onChange={(e, v) => setUnionFromSelection(v)}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        renderInput={(params) => <TextField {...params} label="From Tag (Tracked)" variant="outlined" fullWidth />}
                                        disabled={unionFromOptions.length === 0}
                                    />
                                </Grid>
                                 <Grid item xs={12} sm={5}>
                                    <Autocomplete
                                        multiple limitTags={3}
                                        options={unionToOptions}
                                        getOptionLabel={(option) => option.label}
                                        value={unionToSelection}
                                        onChange={(e, v) => setUnionToSelection(v)}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        disabled={advancedSettings.model_classes.length === 0}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => {
                                                const { key, ...tagProps } = getTagProps({ index });
                                                return (
                                                    <Chip
                                                        key={key}
                                                        variant="outlined"
                                                        label={option.label}
                                                        {...tagProps}
                                                    />
                                                );
                                            })
                                        }
                                        renderInput={(params) => <TextField {...params} label="To Tags (Main Classes)" variant="outlined" fullWidth />}
                                     />
                                 </Grid>
                                 <Grid item xs={12} sm={2}>
                                    <Button variant="contained" onClick={handleAddUnion} startIcon={<AddIcon />} fullWidth disabled={!unionFromSelection || unionToSelection.length === 0}>
                                        Add Union
                                    </Button>
                                </Grid>
                            </Grid>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" gutterBottom>Existing Unions</Typography>
                            {advancedSettings.union?.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {advancedSettings.union.map((unionRule, index) => (
                                        <Chip
                                            key={index}
                                            label={
                                                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Chip size="small" label={getTagName(unionRule[0], allTags)} sx={{ bgcolor: getTagColor(unionRule[0], allTags), color: '#fff' }} />
                                                    +
                                                    {unionRule[1].map(toTagId => (
                                                         <Chip key={toTagId} size="small" label={getTagName(toTagId, allTags)} sx={{ bgcolor: getTagColor(toTagId, allTags), color: '#fff', ml: 0.5 }} />
                                                    ))}
                                                </Box>
                                            }
                                            onDelete={() => handleDeleteUnion(index)}
                                            deleteIcon={<DeleteIcon />}
                                            variant="outlined"
                                            sx={{ p: 1, height: 'auto' }}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Typography>No unions defined.</Typography>
                            )}
                         </TabPanel>

                        <TabPanel value={activeTab} index={3}>
                            <Typography variant="h6" gutterBottom>{editingAnalyzerIndex !== null ? 'Edit Analyzer' : 'Add Analyzer'}</Typography>
                            <Grid container spacing={2} alignItems="center">
                                 <Grid item xs={12} sm={4}>
                                     <TextField label="Analyzer Name" value={analyzerForm.name} onChange={(e) => handleAnalyzerFormChange('name', e.target.value)} fullWidth variant="outlined" />
                                 </Grid>
                                 <Grid item xs={12} sm={4}>
                                     <Autocomplete
                                        options={allTagOptions_derived}
                                        getOptionLabel={(option) => option.label}
                                        value={analyzerForm.class}
                                        onChange={(e,v) => handleAnalyzerFormAutocompleteChange('class', e, v)}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        renderInput={(params) => <TextField {...params} label="For Class" variant="outlined" fullWidth />}
                                     />
                                 </Grid>
                                 <Grid item xs={12} sm={4}>
                                      <Autocomplete
                                        options={analyzerModelPickerOptions}
                                                     getOptionDisabled={(option) => !canManageCustomModels && option?.isCustomProvider}
                                        getOptionLabel={(option) => `${option.label}${option.remaining !== undefined ? ` (${option.remaining} left)` : ''}`}
                                        value={resolveModelPickerOption(analyzerForm.modelValue, analyzerModelPickerOptions)}
                                        onChange={(e,v) => handleAnalyzerFormAutocompleteChange('model', e, v)}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        renderInput={(params) => <TextField {...params} label="Analyzer Model" variant="outlined" fullWidth />}
                                     />
                                 </Grid>
                                 {canManageCustomModels && analyzerResolvedModel.customModel && (
                                     <Grid item xs={12}>
                                         <CustomModelConfigurationPanel
                                            provider={analyzerResolvedModel.customModel.provider}
                                            modelName={analyzerResolvedModel.customModel.modelName}
                                            config={analyzerResolvedModel.config}
                                            title="Analyzer custom model"
                                            onModelNameChange={(value) => setAnalyzerForm((prev) => ({
                                                ...prev,
                                                modelValue: buildCustomModelValue(analyzerResolvedModel.customModel.provider, value),
                                            }))}
                                            onConfigChange={(value) => setAnalyzerForm((prev) => ({
                                                ...prev,
                                                model_capabilities: value.model_capabilities,
                                                model_metadata: value.model_metadata,
                                            }))}
                                         />
                                     </Grid>
                                 )}
                                 {analyzerResolvedModel.customModel && (
                                     <Grid item xs={12}>
                                         <TextField
                                            label="Text Prompt"
                                            value={analyzerForm.text_prompt}
                                            onChange={(e) => handleAnalyzerFormChange('text_prompt', e.target.value)}
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            variant="outlined"
                                            helperText="Write the exact question the custom model should answer for the detected object crop."
                                         />
                                     </Grid>
                                 )}
                                 {analyzerRequiresMarkerSet && (
                                     <Grid item xs={12} sm={4}>
                                         <Autocomplete
                                            options={markerSetOptions}
                                            getOptionLabel={(option) => option.label || ''}
                                            value={analyzerForm.markerSet}
                                            onChange={(e,v) => handleAnalyzerFormAutocompleteChange('markerSet', e, v)}
                                            isOptionEqualToValue={(option, value) => option.value === value.value}
                                            disabled={markerSetSelectDisabled}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Marker Set"
                                                    variant="outlined"
                                                    fullWidth
                                                    helperText={markerSetSelectDisabled ? 'No marker sets available for this item.' : 'Select which marker set calibrates this analyzer.'}
                                                />
                                            )}
                                         />
                                     </Grid>
                                 )}
                                 <Grid item xs={8}>
                                     <TextField
                                        label="Values (comma-separated or paste new lines)"
                                        value={analyzerForm.values}
                                        onChange={(e) => handleAnalyzerFormChange('values', e.target.value)}
                                        onPaste={handleAnalyzerPaste}
                                        fullWidth multiline rows={2} variant="outlined"
                                        InputProps={{
                                            endAdornment: analyzerResolvedModel.option?.hint || analyzerResolvedModel.option?.hint_file_url ? (
                                                <Tooltip title={analyzerResolvedModel.option?.hint}>
                                                    <IconButton onClick={handleOpenHintModal} size="small" edge="end" aria-label="Show hint">
                                                     <InfoIcon color="action" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null
                                        }}
                                        />
                                 </Grid>
                                  <Grid item xs={2}>
                                      <FormControlLabel
                                        control={<Switch checked={analyzerForm.cumulate} onChange={(e) => handleAnalyzerFormSwitchChange('cumulate', e)} />}
                                        label="Cumulate"
                                    />
                                 </Grid>
                                <Grid item xs={2}>
                                    <FormControlLabel
                                        control={<Switch checked={analyzerForm.realtime} onChange={(e) => handleAnalyzerFormSwitchChange('realtime', e)} />}
                                        label="Realtime"
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <FormControlLabel
                                        control={<Switch checked={analyzerForm.get_images} onChange={(e) => handleAnalyzerFormSwitchChange('get_images', e)} />}
                                        label="Get Images"
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <FormControlLabel
                                        control={<Switch checked={analyzerForm.buffering_image} onChange={(e) => handleAnalyzerFormSwitchChange('buffering_image', e)} />}
                                        label="Buffering Image"
                                    />
                                </Grid>
                                {analyzerSupportsEnrichment && (
                                    <Grid item xs={2}>
                                        <FormControlLabel
                                            control={<Switch checked={analyzerForm.enrichment} onChange={(e) => handleAnalyzerFormSwitchChange('enrichment', e)} />}
                                            label="Enrichment"
                                        />
                                    </Grid>
                                )}
                                 <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            variant="contained"
                                            onClick={handleAddAnalyzer}
                                            startIcon={editingAnalyzerIndex !== null ? <EditIcon /> : <AddIcon />}
                                            fullWidth
                                            disabled={analyzerResolvedModel.option?.remaining === 0}
                                        >
                                            {editingAnalyzerIndex !== null ? 'Update Analyzer' : 'Add Analyzer'}
                                        </Button>
                                        {editingAnalyzerIndex !== null && (
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                onClick={handleCancelAnalyzerEdit}
                                            >
                                                Cancel Edit
                                            </Button>
                                        )}
                                    </Box>
                                 </Grid>
                            </Grid>
                             <Divider sx={{ my: 2 }} />
                             <Typography variant="h6" gutterBottom>Existing Analyzers</Typography>
                             {advancedSettings.analyzers?.length > 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {advancedSettings.analyzers.map((analyzer, index) => {
                                        const resolvedMarkerSetId = analyzer.marker_set_id ?? analyzer.marker_set ?? analyzer.markerSet?.id ?? analyzer.markerSet?.value;
                                        const markerSetLabel = resolvedMarkerSetId
                                            ? (getMarkerSetName(resolvedMarkerSetId) || `ID ${resolvedMarkerSetId}`)
                                            : null;
                                        return (
                                            <Paper key={`Adv-analizer-${index}`} variant="outlined" sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                                    <Chip label={`Name: ${analyzer.name}`} />
                                                    <Chip label={`Class: ${getTagName(analyzer.class, allTags)}`} sx={{ bgcolor: getTagColor(analyzer.class, allTags), color: '#fff' }} />
                                                    <Chip label={`Model: ${analyzerModelOptions.find(m => m.value === analyzer.model)?.label || analyzer.model}`} />
                                                     {isCustomModelValue(analyzer.model) && analyzer.model_capabilities && (
                                                         <Chip label={`Caps: in(${analyzer.model_capabilities.in?.join('/') || '-'}) out(${analyzer.model_capabilities.out?.join('/') || '-'})`} />
                                                     )}
                                                    {analyzer.text_prompt && <Chip label={`Prompt: ${analyzer.text_prompt}`} />}
                                                    <Chip label={`Values: ${analyzer.values}`} />
                                                    <Chip label={`Cumulate: ${analyzer.cumulate ? 'Yes' : 'No'}`} />
                                                    <Chip label={`Realtime: ${analyzer?.realtime ? 'Yes' : 'No'}`} />
                                                    <Chip label={`Get Images: ${analyzer?.get_images ? 'Yes' : 'No'}`} />
                                                    <Chip label={`Buffering Image: ${analyzer?.buffering_image ? 'Yes' : 'No'}`} />
                                                    <Chip label={`Enrichment: ${analyzer?.enrichment ? 'Yes' : 'No'}`} />
                                                    {markerSetLabel && (
                                                        <Chip label={`Marker Set: ${markerSetLabel}`} />
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <IconButton onClick={() => handleEditAnalyzer(index)} size="small"><EditIcon /></IconButton>
                                                    <IconButton onClick={() => handleDeleteAnalyzer(index)} size="small"><DeleteIcon /></IconButton>
                                                </Box>
                                            </Paper>
                                        );
                                    })}
                                </Box>
                             ) : (
                                <Typography>No analyzers defined.</Typography>
                             )}
                        </TabPanel>

                        <TabPanel value={activeTab} index={4}>
                                <Typography variant="h6" gutterBottom>{editingDetectorIndex !== null ? 'Edit Detector' : 'Add Detector'}</Typography>
                             <Grid container spacing={2}>
                                 <Grid item xs={12} sm={4}>
                                    <TextField label="Detector Name" value={detectorForm.name} onChange={(e) => handleDetectorFormTextFieldChange('name', e)} fullWidth variant="outlined" />
                                 </Grid>
                                 <Grid item xs={12} sm={4}>
                                      <Autocomplete
                                         options={allTagOptions_derived}
                                         getOptionLabel={(option) => option.label}
                                         value={detectorForm.class}
                                         onChange={(e,v) => handleDetectorFormAutocompleteChange('class', e, v)}
                                         isOptionEqualToValue={(option, value) => option.value === value.value}
                                         renderInput={(params) => <TextField {...params} label="For Class" variant="outlined" fullWidth />}
                                     />
                                 </Grid>
                                 <Grid item xs={12} sm={4}>
                                     <Autocomplete
                                        options={detectorModelPickerOptions}
                                                     getOptionDisabled={(option) => !canManageCustomModels && option?.isCustomProvider}
                                        getOptionLabel={(option) => option.label}
                                        value={resolveModelPickerOption(detectorForm.modelValue, detectorModelPickerOptions)}
                                        onChange={handleDetectorModelChange}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        renderInput={(params) => <TextField {...params} label="Detector Model" variant="outlined" fullWidth />}
                                     />
                                 </Grid>
                                 {canManageCustomModels && detectorResolvedModel.customModel && (
                                     <Grid item xs={12}>
                                         <CustomModelConfigurationPanel
                                            provider={detectorResolvedModel.customModel.provider}
                                            modelName={detectorResolvedModel.customModel.modelName}
                                            config={detectorResolvedModel.config}
                                            title="Detector custom model"
                                            onModelNameChange={(value) => setDetectorForm((prev) => ({
                                                ...prev,
                                                modelValue: buildCustomModelValue(detectorResolvedModel.customModel.provider, value),
                                            }))}
                                            onConfigChange={(value) => setDetectorForm((prev) => ({
                                                ...prev,
                                                model_capabilities: value.model_capabilities,
                                                model_metadata: value.model_metadata,
                                            }))}
                                         />
                                     </Grid>
                                 )}
                                 {detectorResolvedModel.customModel && (
                                     <Grid item xs={12}>
                                         <TextField
                                            label="Text Prompt"
                                            value={detectorForm.text_prompt}
                                            onChange={(e) => handleDetectorFormTextFieldChange('text_prompt', e)}
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            variant="outlined"
                                            helperText="Describe the atypical classes or object parts the custom detector should find inside the detected object crop."
                                         />
                                     </Grid>
                                 )}
                                 <Grid item xs={12} sm={4}>
                                    <Autocomplete multiple limitTags={2}
                                        options={detectorTagOptions_derived}
                                        getOptionLabel={(option) => option.label}
                                        value={getMultiAutocompleteValue(detectorForm.alarms, detectorTagOptions_derived)}
                                        onChange={(e,v) => handleDetectorMultiSelectChange('alarms', e, v)}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        loading={isLoadingDetectorTags}
                                        disabled={!detectorForm.modelValue}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => {
                                                const { key, ...tagProps } = getTagProps({ index });
                                                return (
                                                    <Chip
                                                        key={key}
                                                        variant="outlined"
                                                        label={option.label}
                                                        {...tagProps}
                                                    />
                                                );
                                            })
                                        }
                                        renderInput={(params) => <TextField {...params} label="Detector Alarms Tags" variant="outlined" fullWidth />}
                                    />
                                 </Grid>
                                 <Grid item xs={12} sm={4}>
                                     <Autocomplete multiple limitTags={2}
                                        options={detectorTagOptions_derived}
                                        getOptionLabel={(option) => option.label}
                                        value={getMultiAutocompleteValue(detectorForm.no_alarms, detectorTagOptions_derived)}
                                        onChange={(e,v) => handleDetectorMultiSelectChange('no_alarms', e, v)}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        loading={isLoadingDetectorTags}
                                        disabled={!detectorForm.modelValue}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => {
                                                const { key, ...tagProps } = getTagProps({ index });
                                                return (
                                                    <Chip
                                                        key={key}
                                                        variant="outlined"
                                                        label={option.label}
                                                        {...tagProps}
                                                    />
                                                );
                                            })
                                        }
                                        renderInput={(params) => <TextField {...params} label="Detector No-Alarms Tags" variant="outlined" fullWidth />}
                                    />
                                 </Grid>
                                 <Grid item xs={12} sm={4}>
                                    <Autocomplete multiple limitTags={2}
                                        options={detectorTagOptions_derived}
                                        getOptionLabel={(option) => option.label}
                                        value={getMultiAutocompleteValue(detectorForm.classes, detectorTagOptions_derived)}
                                        onChange={(e,v) => handleDetectorMultiSelectChange('classes', e, v)}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        loading={isLoadingDetectorTags}
                                        disabled={!detectorForm.modelValue}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => {
                                                const { key, ...tagProps } = getTagProps({ index });
                                                return (
                                                    <Chip
                                                        key={key}
                                                        variant="outlined"
                                                        label={option.label}
                                                        {...tagProps}
                                                    />
                                                );
                                            })
                                        }
                                        renderInput={(params) => <TextField {...params} label="Detector Classes Tags" variant="outlined" fullWidth />}
                                    />
                                 </Grid>
                                 <Grid item xs={12} sm={4}>
                                    <FormControlLabel
                                        control={<Switch checked={detectorForm.buffering_image_enabled} onChange={(e) => handleDetectorFormSwitchChange('buffering_image_enabled', e)} />}
                                        label={(
                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                <span>Buffering Image</span>
                                                <Tooltip title="Images might be generated for this classes when detected.">
                                                    <InfoIcon fontSize="small" color="action" />
                                                </Tooltip>
                                            </Box>
                                        )}
                                    />
                                 </Grid>
                                 <Grid item xs={12} sm={8}>
                                    <Autocomplete multiple limitTags={2}
                                        options={detectorBufferingImageOptions}
                                        getOptionLabel={(option) => option.label}
                                        value={getMultiAutocompleteValue(detectorForm.buffering_image, detectorBufferingImageOptions)}
                                        onChange={(e,v) => handleDetectorMultiSelectChange('buffering_image', e, v)}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        disabled={!detectorForm.buffering_image_enabled || detectorBufferingAllowedIds.length === 0}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => {
                                                const { key, ...tagProps } = getTagProps({ index });
                                                return (
                                                    <Chip
                                                        key={key}
                                                        variant="outlined"
                                                        label={option.label}
                                                        {...tagProps}
                                                    />
                                                );
                                            })
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Buffering Image Classes"
                                                variant="outlined"
                                                fullWidth
                                                helperText={detectorBufferingAllowedIds.length === 0 ? 'Select detector classes, alarms, or no-alarms first.' : 'Choose from the detector classes, alarms, or no-alarms selected above.'}
                                            />
                                        )}
                                    />
                                 </Grid>
                                  <Grid item xs={12}>
                                      <Box sx={{ display: 'flex', gap: 1 }}>
                                          <Button variant="contained" onClick={handleAddDetector} startIcon={editingDetectorIndex !== null ? <EditIcon /> : <AddIcon />} fullWidth>{editingDetectorIndex !== null ? 'Update Detector' : 'Add Detector'}</Button>
                                          {editingDetectorIndex !== null && (
                                              <Button variant="outlined" color="secondary" onClick={handleCancelDetectorEdit}>Cancel Edit</Button>
                                          )}
                                      </Box>
                                  </Grid>
                             </Grid>
                             <Divider sx={{ my: 2 }} />
                              <Typography variant="h6" gutterBottom>Existing Detectors</Typography>
                             {advancedSettings.detectors?.length > 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                     {advancedSettings.detectors.map((detector, index) => (
                                        <Paper key={index} variant="outlined" sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                 <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                                     <Chip label={`Name: ${detector.name}`} />
                                                     <Chip label={`Class: ${getTagName(detector.class, allTags)}`} sx={{ bgcolor: getTagColor(detector.class, allTags), color: '#fff' }} />
                                                     <Chip label={`Model: ${detectorModelOptions.find(m => m.value === detector.model)?.label || detector.model}`} />
                                                     {isCustomModelValue(detector.model) && detector.model_capabilities && (
                                                         <Chip label={`Caps: in(${detector.model_capabilities.in?.join('/') || '-'}) out(${detector.model_capabilities.out?.join('/') || '-'})`} />
                                                     )}
                                                     {detector.text_prompt && <Chip label={`Prompt: ${detector.text_prompt}`} />}
                                                 </Box>
                                                 {detector.classes?.length > 0 && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}><Typography variant="caption" sx={{ mr: 0.5 }}>Classes:</Typography> {detector.classes.map(id => <Chip key={id} size="small" label={getTagName(id, allTags)} />)}</Box>}
                                                 {detector.alarms?.length > 0 && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}><Typography variant="caption" sx={{ mr: 0.5 }}>Alarms:</Typography> {detector.alarms.map(id => <Chip key={id} size="small" label={getTagName(id, allTags)} color="warning" />)}</Box>}
                                                 {detector.no_alarms?.length > 0 && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}><Typography variant="caption" sx={{ mr: 0.5 }}>No Alarms:</Typography> {detector.no_alarms.map(id => <Chip key={id} size="small" label={getTagName(id, allTags)} color="default" />)}</Box>}
                                                 {detector.buffering_image?.length > 0 && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}><Typography variant="caption" sx={{ mr: 0.5 }}>Buffering Image:</Typography> {detector.buffering_image.map(id => <Chip key={id} size="small" label={getTagName(id, allTags)} color="info" />)}</Box>}
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <IconButton onClick={() => handleEditDetector(index)} size="small"><EditIcon /></IconButton>
                                                <IconButton onClick={() => handleDeleteDetector(index)} size="small"><DeleteIcon /></IconButton>
                                            </Box>
                                        </Paper>
                                    ))}
                                </Box>
                             ) : (
                                 <Typography>No detectors defined.</Typography>
                             )}
                         </TabPanel>

                    </DialogContent>

                    <DialogActions sx={{ p: 0, pt: 2 }}>
                        <Button onClick={handleClose} color="secondary">Cancel</Button>
                        <Button onClick={handleSave} variant="contained" color="primary">Save Changes</Button>
                    </DialogActions>
                 </Box>
             </Drawer>

            <HintDialog
                open={hintModalOpen}
                onClose={handleCloseHintModal}
                hintText={currentHintData.text}
                hintImageUrl={currentHintData.imageUrl}
            />
        </>
    );
}

export default ZoneAdvancedSettingsModal;
